import styled, { css } from 'styled-components'

import theme from '../theme'

import { Variant, Size, Width } from './Button.types'

type StyledButtonProps = {
  variant: Variant
  size: Size
  width: Width
  hasIcon: boolean
}

const getButtonStylesByVariant = (variant: Variant) => {
  switch (variant) {
    case Variant.WHITE:
      return css`
        color: ${theme.palette.black(20)};
        background-color: ${theme.palette.white};

        &:hover {
          color: ${theme.palette.blue(60)};
          background-color: ${theme.palette.black(94)};
        }

        &:disabled {
          color: ${theme.palette.black(60)};
          background-color: ${theme.palette.black(94)};
        }
      `
    case Variant.BLUE:
      return css`
        color: ${theme.palette.basil(98)};
        background-color: ${theme.palette.blue(60)};

        &:hover {
          background-color: ${theme.palette.blue(70)};
        }

        &:disabled {
          color: ${theme.palette.black(60)};
          background-color: ${theme.palette.black(94)};
        }
      `
    case Variant.BLACK:
      return css`
        color: ${theme.palette.basil(98)};
        background-color: ${theme.palette.black(20)};

        &:hover {
          background-color: ${theme.palette.black(40)};
        }

        &:disabled {
          background-color: ${theme.palette.black(94)};
          color: ${theme.palette.black(60)};
        }
      `
    case Variant.STROKED:
      return css`
        color: ${theme.palette.black(20)};
        background-color: transparent;
        border: 1px solid ${theme.palette.black(20)};

        &:hover {
          background-color: ${theme.palette.blue(94)};
        }

        &:disabled {
          color: ${theme.palette.black(60)};
          border-color: ${theme.palette.black(80)};
        }
      `
    case Variant.TRANSPARENT:
      return css`
        color: ${theme.palette.black(20)};
        background-color: transparent;

        &:hover {
          color: ${theme.palette.blue(60)};
        }

        &:disabled {
          color: ${theme.palette.black(70)};
        }
      `
    default:
      return css``
  }
}

const getButtonStylesBySize = (size: Size) => {
  switch (size) {
    case Size.LARGE:
      return css`
        padding: 0 20px;
        min-height: 50px;
      `
    case Size.MEDIUM:
      return css`
        padding: 0 20px;
        min-height: 40px;
      `
    case Size.SMALL:
      return css`
        padding: 0 10px;
        min-height: 30px;
      `
    default:
      return css``
  }
}

const getButtonStylesByWidth = (width: Width | number | string) => {
  switch (width) {
    case Width.NARROW:
      return css`
        width: fit-content;
      `
    case Width.WIDE:
      return css`
        width: auto;
      `
    default:
      return css`
        ${typeof width === 'number' ? `${width}px` : width};
      `
  }
}

export const Button = styled.button<StyledButtonProps>`
  all: unset;
  box-sizing: border-box;

  padding: 15px 20px;
  width: fit-content;
  height: fit-content;
  border-radius: 5px;
  font-size: 14px;
  line-height: 20px;
  text-align: center;

  cursor: default;

  &:not(:disabled) {
    cursor: pointer;
  }

  transition: border 100ms linear 0s, box-shadow 100ms linear 0s, background-color 100ms linear 0s, color 100ms linear 0s, fill 100ms linear 0s;

  ${({ variant = Variant.BLUE }) => getButtonStylesByVariant(variant)}
  ${({ size = Size.LARGE }) => getButtonStylesBySize(size)}
  ${({ width = Width.NARROW }) => getButtonStylesByWidth(width)}

  ${({ hasIcon }) =>
    hasIcon &&
    css`
      display: flex;
      align-items: center;
      column-gap: 10px;
    `}
`

type StyledLoaderProps = {
  isDisabled?: boolean
}

export const Loader = styled.div<StyledLoaderProps>`
  ${({ isDisabled }) =>
    isDisabled &&
    css`
      div[class^='Loaderstyled__Bubble'] {
        background-color: ${theme.palette.white};
      }
    `})}

`
